import { useEffect, useState, useMemo } from "react";
import { m } from "framer-motion";
import { cn } from "../lib/utils";

const Typewriter = ({
  text,
  speed = 50,
  initialDelay = 0,
  waitTime = 2000,
  deleteSpeed = 30,
  loop = true,
  className,
  showCursor = true,
  hideCursorOnType = false,
  cursorChar = "|",
  cursorClassName = "ml-1",
  cursorAnimationVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.01,
        repeat: Infinity,
        repeatDelay: 0.4,
        repeatType: "reverse",
      },
    },
  },
}) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const entries = useMemo(() => {
    const arr = Array.isArray(text) ? text : [text];
    return arr.map((t) =>
      typeof t === "string" ? { text: t } : t
    );
  }, [text]);

  useEffect(() => {
    let timeout;

    const entry = entries[currentTextIndex];
    const currentText = entry.text;
    const currentDeleteSpeed = entry.deleteSpeed ?? deleteSpeed;
    const currentWaitTime = entry.waitTime ?? waitTime;

    const startTyping = () => {
      if (isDeleting) {
        if (displayText === "") {
          setIsDeleting(false);
          if (currentTextIndex === entries.length - 1 && !loop) {
            return;
          }
          setCurrentTextIndex((prev) => (prev + 1) % entries.length);
          setCurrentIndex(0);
          timeout = setTimeout(() => {}, currentWaitTime);
        } else {
          timeout = setTimeout(() => {
            setDisplayText((prev) => prev.slice(0, -1));
          }, currentDeleteSpeed);
        }
      } else {
        if (currentIndex < currentText.length) {
          timeout = setTimeout(() => {
            setDisplayText((prev) => prev + currentText[currentIndex]);
            setCurrentIndex((prev) => prev + 1);
          }, speed);
        } else if (entries.length > 1) {
          timeout = setTimeout(() => {
            setIsDeleting(true);
          }, currentWaitTime);
        }
      }
    };

    if (currentIndex === 0 && !isDeleting && displayText === "") {
      timeout = setTimeout(startTyping, initialDelay);
    } else {
      startTyping();
    }

    return () => clearTimeout(timeout);
  }, [
    currentIndex,
    displayText,
    isDeleting,
    speed,
    deleteSpeed,
    waitTime,
    entries,
    currentTextIndex,
    loop,
    initialDelay,
  ]);

  return (
    <div className={cn("inline whitespace-pre-wrap tracking-tight", className)} aria-live="polite" aria-atomic="true">
      <span>{displayText}</span>
      {showCursor && (
        <m.span
          variants={cursorAnimationVariants}
          className={cn(
            cursorClassName,
            hideCursorOnType &&
              (currentIndex < entries[currentTextIndex].text.length || isDeleting)
              ? "hidden"
              : ""
          )}
          initial="initial"
          animate="animate"
        >
          {cursorChar}
        </m.span>
      )}
    </div>
  );
};

export { Typewriter };
